import { useEffect, useState } from 'react';
import './AdminModal.css';

/**
 * Shared admin modal chrome. The user, family and church modals each grew their
 * own copy of this; new admin screens use this one instead.
 */
export const AdminModal = ({ title, onClose, children, footer, wide }) => {
  // Escape closing is what people expect of a dialog, and the hand-rolled
  // copies never had it.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div
        className={`admin-modal${wide ? ' is-wide' : ''}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        <div className="admin-modal-header">
          <h2>{title}</h2>
          <button type="button" className="admin-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="admin-modal-body">{children}</div>
        {footer && <div className="admin-modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

export const AdminField = ({ label, hint, children }) => (
  <label className="admin-modal-field">
    <span>{label}</span>
    {children}
    {hint && <em>{hint}</em>}
  </label>
);

export const AdminCheck = ({ label, hint, checked, onChange, disabled }) => (
  <label className="admin-modal-check">
    <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
    <span>{label}</span>
    {hint && <em>{hint}</em>}
  </label>
);

/**
 * Replaces window.confirm and window.prompt for destructive admin actions.
 *
 * `confirmPhrase` turns it into a type-to-confirm dialog, which is what the
 * site-wide resets used prompt() for. Without it, this is a plain confirm.
 */
export const ConfirmModal = ({
  title,
  children,
  confirmLabel = 'Confirm',
  confirmPhrase = null,
  danger = true,
  busy = false,
  // Holds the confirm button off when the dialog cannot be trusted yet,
  // e.g. the impact preview failed to load.
  disabled = false,
  error = null,
  onCancel,
  onConfirm
}) => {
  const [typed, setTyped] = useState('');
  const satisfied = !confirmPhrase || typed === confirmPhrase;

  return (
    <AdminModal
      title={title}
      onClose={onCancel}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`admin-modal-btn ${danger ? 'is-danger' : 'is-primary'}`}
            onClick={onConfirm}
            disabled={!satisfied || busy || disabled}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}
      {children}
      {confirmPhrase && (
        <AdminField label={`Type ${confirmPhrase} to confirm`}>
          <input
            type="text"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
          />
        </AdminField>
      )}
    </AdminModal>
  );
};
